import { DataTable } from "@/components/molecules/tables/data-table";
import type { Customer } from "@/features/customers/types";
import { useQualifications } from "@/features/qualifications/hooks/use-qualifications";
import type { Qualification } from "@/features/qualifications/types";
import { useTranslation } from "@/features/utils/i18n";
import { useQualificationsColumns } from "@/views/backoffice/qualifications/components/columns";
import { FileTextIcon } from "@phosphor-icons/react";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { useParams } from "react-router-dom";

interface CustomerQualificationsTableProps {
  customer: Customer;
  limit?: number;
}

export function CustomerQualificationsTable({
  customer,
  limit,
}: CustomerQualificationsTableProps) {
  const { t } = useTranslation("customers");
  const { company: clientId } = useParams<{ company: string }>();

  // Fetch qualifications for this customer
  const { data, isLoading } = useQualifications({
    customer_id: customer.id,
    limit: limit || 100,
    sort_by: "created_at",
    sort_order: "desc",
  });

  const qualifications = data?.data || [];

  // The contact is the page this table sits on: its column would repeat it
  const baseColumns = useQualificationsColumns();
  const columns: ColumnDef<Qualification>[] = useMemo(
    () =>
      baseColumns.filter(
        (col) =>
          (col as ColumnDef<Qualification> & { accessorKey?: string })
            .accessorKey !== "customer_id",
      ),
    [baseColumns],
  );

  // Limit results if specified
  const displayQualifications = useMemo(() => {
    if (limit && qualifications.length > limit) {
      return qualifications.slice(0, limit);
    }
    return qualifications;
  }, [qualifications, limit]);

  if (displayQualifications.length === 0 && !isLoading) {
    return (
      <div className="text-center py-8">
        <FileTextIcon className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">
          {t("qualifications.noSubmissions")}
        </p>
      </div>
    );
  }

  return (
    <DataTable
      variant="embedded"
      columns={columns}
      data={displayQualifications}
      isLoading={isLoading}
      total={displayQualifications.length}
      onRowNavigate={(qualification) =>
        `/${clientId}/qualifications/${qualification.id}`
      }
    />
  );
}
